import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

const ResendBookingLinkPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/public/bookings/resend-link`,
        { email: email.trim().toLowerCase() }
      );

      if (response.data.ok) {
        setSubmitted(true);
        toast.success('Check your email for the booking link');
      } else {
        toast.error(response.data.message || 'Failed to send link');
      }
    } catch (err) {
      console.error('Error requesting link:', err);
      // Show generic success message for security (don't reveal if email exists)
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 via-white to-zinc-100 dark:from-zinc-900 dark:via-black dark:to-zinc-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {!submitted ? (
          <Card className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${ACCENT}20` }}>
                <svg className="w-8 h-8" style={{ color: ACCENT }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                Resend Booking Link
              </h1>
              <p className="text-zinc-600 dark:text-zinc-400">
                Enter your email to receive a new link to manage your booking
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ '--accent': ACCENT }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: ACCENT }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Link'
                )}
              </button>
            </form>

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-800">
              <p className="text-sm text-zinc-600 dark:text-zinc-400 text-center">
                We'll send a secure link to your email if we find any upcoming bookings.
              </p>
            </div>

            {/* Back Button */}
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-zinc-600 dark:text-zinc-400 hover:text-[--accent] dark:hover:text-[--accent] transition-colors"
                style={{ '--accent': ACCENT }}
              >
                ← Back to Home
              </button>
            </div>
          </Card>
        ) : (
          <Card className="p-8 text-center">
            {/* Success State */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </motion.div>

            <h2 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              Check Your Email
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              If we found any upcoming bookings for <strong>{email}</strong>, we've sent you a secure link.
            </p>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-8">
              Please check your inbox and spam folder.
            </p>

            <button
              onClick={() => {
                setSubmitted(false);
                setEmail('');
              }}
              className="w-full py-3 px-6 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg mb-3"
              style={{ backgroundColor: ACCENT }}
            >
              Send Another Link
            </button>

            <button
              onClick={() => navigate('/')}
              className="text-sm text-zinc-600 dark:text-zinc-400 hover:text-[--accent] dark:hover:text-[--accent] transition-colors"
              style={{ '--accent': ACCENT }}
            >
              Back to Home
            </button>
          </Card>
        )}
      </motion.div>
    </div>
  );
};

export default ResendBookingLinkPage;
